import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentRace, getRaceWeekendSummary } from '../../api';
import LiveRace from '../../components/LiveRace';
import { useAppAlert } from '../../components/AppAlerts';

function formatSource(source) {
    if (!source) return 'source unavailable';
    if (source === 'api') return 'API';
    if (source === 'cached') return 'cached';
    if (source === 'fallback') return 'historical';
    return source;
}

function renderQualifying(rows = []) {
    if (!rows.length) {
        return <div className="small text-muted">No qualifying data available yet.</div>;
    }

    return (
        <table className="table table-sm table-dark table-borderless mb-0">
            <thead>
                <tr>
                    <th>Pos</th>
                    <th>Driver</th>
                    <th>Team</th>
                </tr>
            </thead>
            <tbody>
                {rows.slice(0, 10).map((row, idx) => (
                    <tr key={`${row.driverId || row.driver_id || idx}-${idx}`}>
                        <td>{row.position ?? idx + 1}</td>
                        <td>{row.driverName || row.name || '—'}</td>
                        <td>{row.constructorName || row.team || '—'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function renderRaceLike(title, rows = [], includeTime = false, qualifying = []) {
    if (!rows.length) {
        return (
            <div className="mb-3">
                <h6 className="mb-2">{title}</h6>
                <div className="small text-muted">No race data available yet.</div>
            </div>
        );
    }

    return (
        <div className="mb-3">
            <h6 className="mb-2">{title}</h6>
            <table className="table table-sm table-dark table-borderless mb-0">
                <thead>
                    <tr>
                        <th>Pos</th>
                        <th>Driver</th>
                        <th>Team</th>
                        <th>Grid</th>
                        <th>Laps</th>
                        {includeTime ? <th>Time</th> : null}
                        <th>Status</th>
                        <th>Pts</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.slice(0, 10).map((row, idx) => {
                        const grid = row.grid ?? (qualifying.find((q) => q.driverId === row.driverId || q.driver_id === row.driverId)?.position ?? '—');
                        return (
                            <tr key={`${row.driverId || row.driver_id || idx}-${idx}`}>
                                <td>{row.position ?? '—'}</td>
                                <td>{row.driverName || row.name || '—'}</td>
                                <td>{row.constructorName || row.team || '—'}</td>
                                <td>{grid}</td>
                                <td>{row.laps ?? '—'}</td>
                                {includeTime ? <td>{row.time || '—'}</td> : null}
                                <td>{row.status || '—'}</td>
                                <td>{row.points ?? '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function renderHistoricalFallback(raceResults = [], qualifying = []) {
    return (
        <>
            <div className="mb-3">
                <h6 className="mb-2">Historical Qualifying</h6>
                {renderQualifying(qualifying)}
            </div>
            {renderRaceLike('Historical Race Results', raceResults, true, qualifying)}
        </>
    );
}

export default function Home() {
    const [weekendSummary, setWeekendSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isLive, setIsLive] = useState(false);
    const notify = useAppAlert();
    const handleLiveChange = useCallback((live) => setIsLive(live), []);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');

        getCurrentRace()
            .then((raceId) => {
                if (!raceId) return null;
                return getRaceWeekendSummary(raceId);
            })
            .then((payload) => {
                if (!active) return;
                setWeekendSummary(payload || null);
            })
            .catch((e) => {
                if (!active) return;
                const message = e.message || 'Unable to load weekend data';
                setError(message);
                notify(message, 'error');
            })
            .finally(() => {
                if (!active) return;
                setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [notify]);

    const isFallback = weekendSummary && weekendSummary.mode === 'fallback';
    const isNone = weekendSummary && weekendSummary.mode === 'none';
    const current = weekendSummary && weekendSummary.current;
    const fallback = weekendSummary && weekendSummary.fallback;
    const sources = weekendSummary && weekendSummary.sources;

    return (
        <div className="home-page">
            <div className="card">
                <div className="card-body">
                    <h2 className="card-title">Welcome to F1 Picks</h2>
                    <p className="card-text">Enter your predictions for the race and compete on the leaderboard.</p>
                    <Link to="/picks" className="btn btn-primary me-2">Enter Picks</Link>
                    <Link to="/leaderboard" className="btn btn-secondary">Leaderboard</Link>
                </div>
            </div>

            <LiveRace onLiveChange={handleLiveChange} />

            {!isLive && <div className="card mt-3">
                <div className="card-body">
                    <h5 className="card-title">Current Race Weekend</h5>
                    {loading ? <div className="small text-muted">Loading weekend data…</div> : null}

                    {!loading && !error && weekendSummary ? (
                        <>
                            <div className="small text-muted mb-2">
                                {isFallback
                                    ? `No ${current?.season || '2026'} session results yet. Showing ${fallback?.season || 'previous year'} qualifying and race results for this circuit.`
                                    : isNone
                                        ? `No session results available yet for ${current?.raceName || 'this race'}.`
                                        : `Showing ${current?.season || ''} ${current?.raceName || 'race'} session data.`}
                            </div>

                            {isFallback ? (
                                <>
                                    <div className="mb-3">
                                        <h6 className="mb-2">Historical Race ({fallback?.season || 'Previous Year'})</h6>
                                        {renderHistoricalFallback((fallback && fallback.raceResults) || [], (fallback && fallback.qualifying) || [])}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="mb-3">
                                        <h6 className="mb-2">Qualifying {sources ? <span className="text-muted fw-normal">{formatSource(sources.qualifying)}</span> : null}</h6>
                                        {renderQualifying((current && current.qualifying) || [])}
                                    </div>
                                    {renderRaceLike(
                                        <span>Sprint {sources ? <span className="text-muted fw-normal">{formatSource(sources.sprint)}</span> : null}</span>,
                                        (current && current.sprint) || [], false, (current && current.qualifying) || []
                                    )}
                                    {renderRaceLike(
                                        <span>Race {sources ? <span className="text-muted fw-normal">{formatSource(sources.raceResults)}</span> : null}</span>,
                                        (current && current.raceResults) || [], true, (current && current.qualifying) || []
                                    )}
                                </>
                            )}
                        </>
                    ) : null}

                    {!loading && !error && !weekendSummary ? (
                        <div className="small text-muted">No race selected yet.</div>
                    ) : null}
                </div>
            </div>}
        </div>
    );
}
